import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import type {
  AudioAsset,
  Chat,
  OutlineBeat,
  PromptTemplateId,
  Settings,
  Slug,
  Stage,
  Story,
  Universe,
  Vibe,
} from '../types';
import { DEFAULT_SETTINGS, DEFAULT_VOICE_ID } from '../types';
import { setExtensionsLookup, setOverridesLookup } from '../templates/registry';

type UIState = {
  activeUniverseId?: string;
  activeStoryByUniverse: Record<string, string | undefined>;
};

export type BootstrapStatus = 'pending' | 'ready';

type AppState = {
  settings: Settings;
  universes: Record<string, Universe>;
  slugPool: Slug[];
  stories: Record<string, Story>;
  chats: Record<string, Chat>;
  ui: UIState;
  bootstrapStatus: BootstrapStatus;

  setSettings: (patch: Partial<Settings>) => void;
  setBootstrapStatus: (status: BootstrapStatus) => void;

  createUniverse: (init: {
    name: string;
    defaultProtagonists?: string;
    description?: string;
  }) => Universe;
  updateUniverse: (id: string, patch: Partial<Universe>) => void;
  archiveUniverse: (id: string) => void;
  unarchiveUniverse: (id: string) => void;

  setUniverseOverride: (
    universeId: string,
    templateId: PromptTemplateId,
    text: string
  ) => void;
  clearUniverseOverride: (universeId: string, templateId: PromptTemplateId) => void;
  setUniverseExtension: (
    universeId: string,
    templateId: PromptTemplateId,
    text: string
  ) => void;
  clearUniverseExtension: (universeId: string, templateId: PromptTemplateId) => void;

  setBatchVibe: (universeId: string, vibe: Vibe) => void;
  setBatchGuidance: (universeId: string, text: string) => void;

  setUniverseCanon: (universeId: string, canon: string, sourceStoryId?: string) => void;
  setUniverseCanonInFlight: (universeId: string, inFlight: boolean) => void;

  addSlugs: (slugs: Slug[]) => void;
  hideSlug: (slugId: string) => void;

  createStory: (story: Story) => void;
  updateStory: (id: string, patch: Partial<Story>) => void;
  setStoryOutline: (id: string, beats: OutlineBeat[], raw?: string) => void;
  setStoryProseKey: (id: string, proseKey: string) => void;
  setStoryAudio: (id: string, audio: AudioAsset) => void;
  setStoryStage: (id: string, stage: Stage) => void;
  setInFlight: (id: string, stage: Stage | undefined) => void;
  appendRevision: (id: string, stage: Stage, notes: string) => void;
  setStoryVoiceOverride: (id: string, voiceId: string | undefined) => void;
  archiveStory: (id: string) => void;
  unarchiveStory: (id: string) => void;

  setActiveUniverse: (id: string | undefined) => void;
  setActiveStoryForUniverse: (
    universeId: string,
    storyId: string | undefined
  ) => void;

  createChat: (universeId: string, title?: string) => Chat;
  renameChat: (chatId: string, title: string) => void;
  touchChat: (chatId: string, messageCount?: number) => void;
  deleteChat: (chatId: string) => void;
  updateChatUniverse: (chatId: string, universeId: string) => void;
};

function patchUniverse(
  state: { universes: Record<string, Universe> },
  id: string,
  fn: (u: Universe) => Universe
) {
  const existing = state.universes[id];
  if (!existing) return {};
  return {
    universes: {
      ...state.universes,
      [id]: { ...fn(existing), updatedAt: Date.now() },
    },
  };
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      settings: DEFAULT_SETTINGS,
      universes: {},
      slugPool: [],
      stories: {},
      chats: {},
      ui: { activeStoryByUniverse: {} },
      bootstrapStatus: 'pending',

      setSettings: (patch) =>
        set((s) => ({ settings: { ...s.settings, ...patch } })),
      setBootstrapStatus: (status) => set({ bootstrapStatus: status }),

      createUniverse: (init) => {
        const now = Date.now();
        const u: Universe = {
          id: nanoid(10),
          name: init.name.trim() || 'Untitled universe',
          description: init.description,
          defaultProtagonists: init.defaultProtagonists ?? '',
          defaultVoiceId: DEFAULT_VOICE_ID,
          overrides: {},
          extensions: {},
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({ universes: { ...s.universes, [u.id]: u } }));
        return u;
      },
      updateUniverse: (id, patch) =>
        set((s) => patchUniverse(s, id, (u) => ({ ...u, ...patch }))),
      archiveUniverse: (id) =>
        set((s) => patchUniverse(s, id, (u) => ({ ...u, hidden: true }))),
      unarchiveUniverse: (id) =>
        set((s) => patchUniverse(s, id, (u) => ({ ...u, hidden: false }))),

      setUniverseOverride: (universeId, templateId, text) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => ({
            ...u,
            overrides: { ...u.overrides, [templateId]: text },
          }))
        ),
      clearUniverseOverride: (universeId, templateId) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => {
            const { [templateId]: _drop, ...rest } = u.overrides;
            return { ...u, overrides: rest };
          })
        ),
      setUniverseExtension: (universeId, templateId, text) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => ({
            ...u,
            extensions: { ...u.extensions, [templateId]: text },
          }))
        ),
      clearUniverseExtension: (universeId, templateId) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => {
            const { [templateId]: _drop, ...rest } = u.extensions;
            return { ...u, extensions: rest };
          })
        ),

      setBatchVibe: (universeId, vibe) =>
        set((s) => patchUniverse(s, universeId, (u) => ({ ...u, batchVibe: vibe }))),
      setBatchGuidance: (universeId, text) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => ({ ...u, batchGuidance: text }))
        ),

      setUniverseCanon: (universeId, canon, sourceStoryId) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => ({
            ...u,
            canon,
            canonUpdatedAt: Date.now(),
            canonLastSourceStoryId: sourceStoryId ?? u.canonLastSourceStoryId,
          }))
        ),
      setUniverseCanonInFlight: (universeId, inFlight) =>
        set((s) =>
          patchUniverse(s, universeId, (u) => ({ ...u, canonInFlight: inFlight }))
        ),

      addSlugs: (slugs) =>
        set((s) => ({ slugPool: [...slugs, ...s.slugPool] })),
      hideSlug: (slugId) =>
        set((s) => ({
          slugPool: s.slugPool.map((slug) =>
            slug.id === slugId ? { ...slug, hidden: true } : slug
          ),
        })),

      createStory: (story) =>
        set((s) => ({ stories: { ...s.stories, [story.id]: story } })),
      updateStory: (id, patch) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: { ...existing, ...patch, updatedAt: Date.now() },
            },
          };
        }),
      setStoryOutline: (id, beats, raw) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: {
                ...existing,
                outline: beats,
                outlineText: raw ?? existing.outlineText,
                stage: existing.stage === 'slug' ? 'outline' : existing.stage,
                updatedAt: Date.now(),
              },
            },
          };
        }),
      setStoryProseKey: (id, proseKey) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: {
                ...existing,
                proseKey,
                stage:
                  existing.stage === 'slug' || existing.stage === 'outline'
                    ? 'prose'
                    : existing.stage,
                updatedAt: Date.now(),
              },
            },
          };
        }),
      setStoryAudio: (id, audio) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: {
                ...existing,
                audio,
                stage: 'ready',
                updatedAt: Date.now(),
              },
            },
          };
        }),
      setStoryStage: (id, stage) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: { ...existing, stage, updatedAt: Date.now() },
            },
          };
        }),
      setInFlight: (id, stage) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: { ...existing, inFlight: stage, updatedAt: Date.now() },
            },
          };
        }),
      appendRevision: (id, stage, notes) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: {
                ...existing,
                revisionLog: [
                  ...existing.revisionLog,
                  { stage, notes, at: Date.now() },
                ],
                updatedAt: Date.now(),
              },
            },
          };
        }),
      setStoryVoiceOverride: (id, voiceId) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: {
                ...existing,
                voiceIdOverride: voiceId,
                updatedAt: Date.now(),
              },
            },
          };
        }),
      archiveStory: (id) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: { ...existing, hidden: true, updatedAt: Date.now() },
            },
          };
        }),
      unarchiveStory: (id) =>
        set((s) => {
          const existing = s.stories[id];
          if (!existing) return {};
          return {
            stories: {
              ...s.stories,
              [id]: { ...existing, hidden: false, updatedAt: Date.now() },
            },
          };
        }),

      setActiveUniverse: (id) =>
        set((s) => ({ ui: { ...s.ui, activeUniverseId: id } })),
      setActiveStoryForUniverse: (universeId, storyId) =>
        set((s) => ({
          ui: {
            ...s.ui,
            activeStoryByUniverse: {
              ...s.ui.activeStoryByUniverse,
              [universeId]: storyId,
            },
          },
        })),

      createChat: (universeId, title) => {
        const now = Date.now();
        const chat: Chat = {
          id: nanoid(10),
          universeId,
          title: title?.trim() || 'New chat',
          createdAt: now,
          lastUsedAt: now,
          messageCount: 0,
        };
        set((s) => ({ chats: { ...s.chats, [chat.id]: chat } }));
        return chat;
      },
      renameChat: (chatId, title) =>
        set((s) => {
          const existing = s.chats[chatId];
          if (!existing) return {};
          return {
            chats: {
              ...s.chats,
              [chatId]: { ...existing, title: title.trim() || existing.title },
            },
          };
        }),
      touchChat: (chatId, messageCount) =>
        set((s) => {
          const existing = s.chats[chatId];
          if (!existing) return {};
          return {
            chats: {
              ...s.chats,
              [chatId]: {
                ...existing,
                lastUsedAt: Date.now(),
                messageCount: messageCount ?? existing.messageCount,
              },
            },
          };
        }),
      deleteChat: (chatId) =>
        set((s) => {
          const { [chatId]: _drop, ...rest } = s.chats;
          return { chats: rest };
        }),
      updateChatUniverse: (chatId, universeId) =>
        set((s) => {
          const existing = s.chats[chatId];
          if (!existing) return {};
          return {
            chats: {
              ...s.chats,
              [chatId]: { ...existing, universeId },
            },
          };
        }),
    }),
    {
      name: 'bedtimer-app',
      version: 3,
      partialize: (s) => ({
        settings: s.settings,
        universes: s.universes,
        slugPool: s.slugPool,
        stories: s.stories,
        chats: s.chats,
        ui: s.ui,
      }),
      migrate: (persisted, version) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const old = (persisted ?? {}) as any;
        if (version >= 3) return old;
        if (version === 2) return { ...old, chats: old.chats ?? {} };
        // v < 2 is no longer supported. Fresh-install initial state will apply.
        return {};
      },
    }
  )
);

setOverridesLookup((id, universeId) => {
  const state = useAppStore.getState();
  const target = universeId ?? state.ui.activeUniverseId;
  if (!target) return undefined;
  return state.universes[target]?.overrides[id];
});
setExtensionsLookup((id, universeId) => {
  const state = useAppStore.getState();
  const target = universeId ?? state.ui.activeUniverseId;
  if (!target) return undefined;
  return state.universes[target]?.extensions[id];
});
